import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ConsultationBooking = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    services: [],
    budget: '',
    timeline: '',
    message: '',
    preferredTime: '',
    consultationType: 'video'
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const serviceOptions = [
    { value: 'social-media', label: 'Social Media Marketing' },
    { value: 'web-development', label: 'Web Development' },
    { value: 'app-development', label: 'App Development' },
    { value: 'all-services', label: 'Complete Digital Transformation' }
  ];

  const budgetOptions = [
    { value: 'under-10K', label: 'Under ₹10,000' },
    { value: '10K-25K', label: '₹10,000 - ₹25,000' },
    { value: '25K-50k', label: '₹25,000 - ₹50,000' },
    { value: '50k-100k', label: '₹50,000 - ₹100,000' },
    { value: 'over-100k', label: 'Over ₹100,000' }
  ];

  const timelineOptions = [
    { value: 'asap', label: 'ASAP (Rush Project)' },
    { value: '1-month', label: 'Within 1 Month' },
    { value: '2-3-months', label: '2-3 Months' },
    { value: '3-6-months', label: '3-6 Months' },
    { value: 'flexible', label: 'Flexible Timeline' }
  ];

  const timeSlots = [
    { value: 'morning', label: '9:00 AM - 12:00 PM', icon: 'Sun' },
    { value: 'afternoon', label: '12:00 PM - 5:00 PM', icon: 'Clock' },
    { value: 'evening', label: '5:00 PM - 8:00 PM', icon: 'Moon' }
  ];

  const consultationTypes = [
    { value: 'video', label: 'Video Call', icon: 'Video', description: 'Face-to-face discussion via Zoom/Meet' },
    { value: 'phone', label: 'Phone Call', icon: 'Phone', description: 'Traditional phone conversation' },
    { value: 'in-person', label: 'In-Person', icon: 'MapPin', description: 'Meet at our office or your location' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServiceToggle = (serviceValue) => {
    setFormData(prev => ({
      ...prev,
      services: prev?.services?.includes(serviceValue)
        ? prev?.services?.filter(s => s !== serviceValue)
        : [...prev?.services, serviceValue]
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    // Mock submission
    setIsSubmitted(true);
  };

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  if (isSubmitted) {
    return (
      <section className="py-20 bg-gradient-to-br from-primary via-primary/95 to-secondary">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl p-12 brand-shadow">
            <div className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={40} className="text-success-foreground" />
            </div>
            
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Consultation Booked Successfully!
            </h2>
            
            <p className="text-xl text-muted-foreground mb-8">
              Thank you for choosing Singh Creations. We'll contact you within 24 hours to confirm your consultation details.
            </p>
            
            <div className="bg-surface rounded-xl p-6 mb-8">
              <h3 className="font-bold text-foreground mb-4">What happens next?</h3>
              <div className="grid md:grid-cols-3 gap-6 text-left">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-accent-foreground font-bold text-sm">1</div>
                  <div>
                    <p className="font-medium text-foreground">Confirmation Call</p>
                    <p className="text-sm text-muted-foreground">We'll call to confirm your preferred time slot</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-accent-foreground font-bold text-sm">2</div>
                  <div>
                    <p className="font-medium text-foreground">Preparation</p>
                    <p className="text-sm text-muted-foreground">We'll prepare a customized presentation for you</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-accent-foreground font-bold text-sm">3</div>
                  <div>
                    <p className="font-medium text-foreground">Consultation</p>
                    <p className="text-sm text-muted-foreground">60-minute strategy session with our experts</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="default" iconName="Calendar" iconPosition="left">
                Add to Calendar
              </Button>
              <Button variant="outline" iconName="Download" iconPosition="left">
                Download Confirmation
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-primary via-primary/95 to-secondary">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-accent/20 text-accent-foreground px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Calendar" size={16} />
            <span>Free Consultation</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
            Book Your Strategy Session
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Get personalized recommendations and a custom digital strategy in a 60-minute consultation with our experts.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex items-center space-x-4">
            {[1, 2, 3]?.map((step) => (
              <React.Fragment key={step}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  currentStep >= step 
                    ? 'bg-accent text-accent-foreground' 
                    : 'bg-white/20 text-white/60'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`w-12 h-1 rounded-full ${
                    currentStep > step ? 'bg-accent' : 'bg-white/20'
                  }`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 brand-shadow">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-foreground mb-6">Tell Us About Yourself</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <Input
                    label="Full Name"
                    type="text"
                    placeholder="John Doe"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    required
                  />
                  
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="john@company.com"
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    required
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <Input
                    label="Phone Number"
                    type="tel"
                    placeholder="+91-7982843491"
                    value={formData?.phone}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    required
                  />
                  
                  <Input
                    label="Company Name"
                    type="text"
                    placeholder="Your Company"
                    value={formData?.company}
                    onChange={(e) => handleInputChange('company', e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-4">
                    Which services interest you? (Select all that apply)
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {serviceOptions?.map((service) => (
                      <button
                        key={service?.value}
                        type="button"
                        onClick={() => handleServiceToggle(service?.value)}
                        className={`p-4 rounded-lg border brand-transition text-left ${
                          formData?.services?.includes(service?.value)
                            ? 'border-accent bg-accent/5 text-accent' :'border-border hover:border-accent/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{service?.label}</span>
                          {formData?.services?.includes(service?.value) && (
                            <Icon name="Check" size={20} className="text-accent" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Project Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-foreground mb-6">Project Details</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">
                      Budget Range
                    </label>
                    <div className="space-y-2">
                      {budgetOptions?.map((budget) => (
                        <button
                          key={budget?.value}
                          type="button"
                          onClick={() => handleInputChange('budget', budget?.value)}
                          className={`w-full p-3 rounded-lg border brand-transition text-left ${
                            formData?.budget === budget?.value
                              ? 'border-accent bg-accent/5 text-accent' :'border-border hover:border-accent/50'
                          }`}
                        >
                          {budget?.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">
                      Project Timeline
                    </label>
                    <div className="space-y-2">
                      {timelineOptions?.map((timeline) => (
                        <button
                          key={timeline?.value}
                          type="button"
                          onClick={() => handleInputChange('timeline', timeline?.value)}
                          className={`w-full p-3 rounded-lg border brand-transition text-left ${
                            formData?.timeline === timeline?.value
                              ? 'border-accent bg-accent/5 text-accent' :'border-border hover:border-accent/50'
                          }`}
                        >
                          {timeline?.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Tell us more about your project
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Describe your goals, challenges, and what you hope to achieve..."
                    value={formData?.message}
                    onChange={(e) => handleInputChange('message', e?.target?.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent brand-transition"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Consultation Preferences */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-foreground mb-6">Consultation Preferences</h3>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-4">
                    Preferred Consultation Type
                  </label>
                  <div className="grid md:grid-cols-3 gap-4">
                    {consultationTypes?.map((type) => (
                      <button
                        key={type?.value}
                        type="button"
                        onClick={() => handleInputChange('consultationType', type?.value)}
                        className={`p-4 rounded-lg border brand-transition text-center ${
                          formData?.consultationType === type?.value
                            ? 'border-accent bg-accent/5 text-accent' :'border-border hover:border-accent/50'
                        }`}
                      >
                        <Icon name={type?.icon} size={32} className="mx-auto mb-2" />
                        <p className="font-medium mb-1">{type?.label}</p>
                        <p className="text-xs text-muted-foreground">{type?.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-4">
                    Preferred Time Slot
                  </label>
                  <div className="grid md:grid-cols-3 gap-4">
                    {timeSlots?.map((slot) => (
                      <button
                        key={slot?.value}
                        type="button"
                        onClick={() => handleInputChange('preferredTime', slot?.value)}
                        className={`p-4 rounded-lg border brand-transition text-center ${
                          formData?.preferredTime === slot?.value
                            ? 'border-accent bg-accent/5 text-accent' :'border-border hover:border-accent/50'
                        }`}
                      >
                        <Icon name={slot?.icon} size={24} className="mx-auto mb-2" />
                        <p className="font-medium">{slot?.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-accent/10 rounded-xl p-6">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={20} className="text-accent mt-1" />
                    <div>
                      <h4 className="font-medium text-foreground mb-2">What to Expect</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• 60-minute strategy session with our senior team</li>
                        <li>• Personalized recommendations for your business</li>
                        <li>• Custom project timeline and pricing</li>
                        <li>• No obligation - completely free consultation</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              <div>
                {currentStep > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    iconName="ChevronLeft"
                    iconPosition="left"
                  >
                    Previous
                  </Button>
                )}
              </div>
              
              <div className="flex items-center space-x-4">
                <span className="text-sm text-muted-foreground">
                  Step {currentStep} of 3
                </span>
                
                {currentStep < 3 ? (
                  <Button
                    type="button"
                    variant="default"
                    onClick={nextStep}
                    iconName="ChevronRight"
                    iconPosition="right"
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="default"
                    className="bg-accent hover:bg-accent/90 text-accent-foreground"
                    iconName="Calendar"
                    iconPosition="left"
                  >
                    Book Consultation
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ConsultationBooking;